import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Panel, LoadingState, ErrorState } from "@/components/common";
import { Library, Lock } from "lucide-react";
import { ChapterList, SourcePanel, AddRuleForm, RulesTable, Lifecycle, AuditList, IntegrityBanner } from "@/components/gtfregistry/RegistryParts";

function ChapterDetail({ module, ov }) {
  const q = useQuery({ queryKey: ["gtfr-detail", module], queryFn: async () => (await api.get(`/gtf-registry/chapters/${module}`)).data });
  if (q.isLoading) return <LoadingState label="Loading chapter…" />;
  if (q.isError) return <ErrorState label="Chapter could not be loaded" onRetry={q.refetch} />;
  const d = q.data;
  return (
    <>
      <Panel title={`Module ${module} lifecycle`} testId="gtfr-lifecycle-panel"><div className="p-4"><Lifecycle module={module} detail={d} states={ov.states} /></div></Panel>
      <Panel title="Source transcriptions (insert-only, SHA-256 fingerprinted)" testId="gtfr-source-panel"><div className="p-4"><SourcePanel module={module} detail={d} /></div></Panel>
      <Panel title="Rule catalogue" testId="gtfr-rules-panel">
        <div className="p-4 space-y-4">
          {!d.chapter.signals_allowed && <div data-testid="gtfr-no-signals" className="text-[11px] text-sky-200">Module 0 holds no executable trading signals: only contextual, educational or unresolved entries can be recorded.</div>}
          <AddRuleForm module={module} detail={d} classes={ov.classes} />
          <RulesTable detail={d} classes={ov.classes} />
        </div>
      </Panel>
    </>
  );
}

export default function GTFRegistry() {
  const [sel, setSel] = useState(0);
  const q = useQuery({ queryKey: ["gtfr-overview"], queryFn: async () => (await api.get("/gtf-registry/overview")).data });
  return (
    <div data-testid="gtf-registry-page" className="space-y-4">
      <PageHeader title="GTF Methodology Registry" subtitle="One chapter at a time · every rule traced to the original transcription" icon={Library} demo={false} testId="gtfr-header" />
      {q.isLoading && <LoadingState label="Loading registry…" />}
      {q.isError && <ErrorState label="Registry could not be loaded" onRetry={q.refetch} />}
      {q.data && (
        <>
          <div data-testid="gtfr-exec-banner" className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-200">
            <Lock className="w-4 h-4 mt-0.5 shrink-0" /> Strategy execution is disabled{q.data.strategy_execution_enabled ? "" : " on this server"}. Registry entries never place orders or produce live signals; unresolved or unapproved rules can never become executable.
          </div>
          <IntegrityBanner integrity={q.data.integrity} />
          <Panel title="Chapters" testId="gtfr-chapters-panel"><div className="p-4"><ChapterList data={q.data} selected={sel} onSelect={setSel} /></div></Panel>
          {q.data.chapters.some((c) => c.module === sel) && <ChapterDetail module={sel} ov={q.data} />}
          <Panel title="Registry audit" testId="gtfr-audit-panel"><div className="p-4"><AuditList /></div></Panel>
        </>
      )}
    </div>
  );
}
